import { getLocalStorage, saveLocalStorage } from "@elit-hair/elit-tools";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";

export type AudioDeviceKind = "audioinput" | "audiooutput";

export type AudioDeviceAutoSwitch = {
    kind: AudioDeviceKind;
    previousDeviceId: string | null;
    newDeviceId: string;
    previousLabel: string | null;
    newLabel: string;
};

export type ResolvedAudioDevices = {
    inputDeviceId: string | null;
    outputDeviceId: string | null;
    isComplete: boolean;
    autoSwitches: AudioDeviceAutoSwitch[];
};

export type AudioDeviceListDiff = {
    added: MediaDeviceInfo[];
    removed: MediaDeviceInfo[];
};

const AUDIO_ERROR_KEYWORDS = [
    "device",
    "audio",
    "microphone",
    "media",
    "getusermedia",
    "acquisition",
    "notfound",
    "notreadable",
    "overconstrained",
    "permission denied",
    "sinkid",
    "speaker",
] as const;

const AUDIO_DEVICE_KINDS: AudioDeviceKind[] = ["audioinput", "audiooutput"];

export function isAudioRelatedFailureMessage(message: string): boolean {
    const msg = message.toLowerCase();
    return AUDIO_ERROR_KEYWORDS.some((keyword) => msg.includes(keyword));
}

export function getDeviceLabel(
    devices: MediaDeviceInfo[],
    deviceId: string,
    kind: AudioDeviceKind,
    unnamedLabel: string
): string {
    const device = devices.find((entry) => entry.deviceId === deviceId && entry.kind === kind);
    const label = device?.label?.trim();
    return label || unnamedLabel;
}

function pickDefaultDeviceId(devices: MediaDeviceInfo[], kind: AudioDeviceKind): string | null {
    const filtered = devices.filter((device) => device.kind === kind);
    if (filtered.length === 0) {
        return null;
    }

    const defaultDevice = filtered.find((device) => device.label.toLowerCase().includes("default"));
    if (defaultDevice) {
        return defaultDevice.deviceId;
    }

    return filtered[0]?.deviceId ?? null;
}

function deviceExists(devices: MediaDeviceInfo[], deviceId: string, kind: AudioDeviceKind): boolean {
    return devices.some((device) => device.deviceId === deviceId && device.kind === kind);
}

function filterAudioDevices(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
    return devices.filter((device) => device.kind === "audioinput" || device.kind === "audiooutput");
}

export function detectAudioDeviceListDiff(
    previousDevices: MediaDeviceInfo[] | null,
    currentDevices: MediaDeviceInfo[]
): AudioDeviceListDiff {
    if (!previousDevices) {
        return { added: [], removed: [] };
    }

    const previousAudio = filterAudioDevices(previousDevices);
    const currentAudio = filterAudioDevices(currentDevices);
    const previousIds = new Set(previousAudio.map((device) => device.deviceId));

    return {
        added: currentAudio.filter((device) => !previousIds.has(device.deviceId)),
        removed: previousAudio.filter((device) => !currentAudio.some((entry) => entry.deviceId === device.deviceId)),
    };
}

export async function enumerateAudioDevices(): Promise<MediaDeviceInfo[]> {
    let devices = await navigator.mediaDevices.enumerateDevices();
    const hasInputLabels = devices.some((device) => device.kind === "audioinput" && device.label);

    if (hasInputLabels) {
        return devices;
    }

    let stream: MediaStream | null = null;
    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        devices = await navigator.mediaDevices.enumerateDevices();
    } finally {
        stream?.getTracks().forEach((track) => track.stop());
    }

    return devices;
}

export function persistResolvedAudioDevices(resolved: ResolvedAudioDevices): void {
    if (resolved.inputDeviceId) {
        saveLocalStorage(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key, resolved.inputDeviceId);
    }
    if (resolved.outputDeviceId) {
        saveLocalStorage(LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key, resolved.outputDeviceId);
    }
}

export function resolveAudioDeviceIds(
    devices: MediaDeviceInfo[],
    storedInputId: string | null,
    storedOutputId: string | null,
    options?: { persistResolved?: boolean; unnamedLabel?: string; requireOutputDevice?: boolean }
): ResolvedAudioDevices {
    const requireOutputDevice = options?.requireOutputDevice ?? true;
    const unnamedLabel = options?.unnamedLabel ?? "Unnamed device";
    const autoSwitches: AudioDeviceAutoSwitch[] = [];

    const storedInput = storedInputId?.trim() || null;
    const storedOutput = storedOutputId?.trim() || null;

    let inputDeviceId = storedInput;
    let outputDeviceId = storedOutput;

    const inputWasStale = Boolean(inputDeviceId && !deviceExists(devices, inputDeviceId, "audioinput"));
    const outputWasStale = Boolean(outputDeviceId && !deviceExists(devices, outputDeviceId, "audiooutput"));

    if (inputWasStale) {
        inputDeviceId = null;
    }

    if (outputWasStale) {
        outputDeviceId = null;
    }

    if (!inputDeviceId) {
        inputDeviceId = pickDefaultDeviceId(devices, "audioinput");
    }

    if (!outputDeviceId) {
        outputDeviceId = pickDefaultDeviceId(devices, "audiooutput");
    }

    if (inputDeviceId && (!storedInput || inputWasStale) && inputDeviceId !== storedInput) {
        autoSwitches.push({
            kind: "audioinput",
            previousDeviceId: storedInput,
            newDeviceId: inputDeviceId,
            previousLabel: storedInput ? getDeviceLabel(devices, storedInput, "audioinput", unnamedLabel) : null,
            newLabel: getDeviceLabel(devices, inputDeviceId, "audioinput", unnamedLabel),
        });
    }

    if (outputDeviceId && (!storedOutput || outputWasStale) && outputDeviceId !== storedOutput) {
        autoSwitches.push({
            kind: "audiooutput",
            previousDeviceId: storedOutput,
            newDeviceId: outputDeviceId,
            previousLabel: storedOutput ? getDeviceLabel(devices, storedOutput, "audiooutput", unnamedLabel) : null,
            newLabel: getDeviceLabel(devices, outputDeviceId, "audiooutput", unnamedLabel),
        });
    }

    const resolved: ResolvedAudioDevices = {
        inputDeviceId,
        outputDeviceId,
        isComplete: Boolean(inputDeviceId && (outputDeviceId || !requireOutputDevice)),
        autoSwitches,
    };

    if (options?.persistResolved) {
        persistResolvedAudioDevices(resolved);
    }

    return resolved;
}

export async function resolveStoredAudioDevices(options?: {
    persistResolved?: boolean;
    unnamedLabel?: string;
    requireOutputDevice?: boolean;
}): Promise<ResolvedAudioDevices & { devices: MediaDeviceInfo[] }> {
    const storedInputId = getLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key,
        null
    );
    const storedOutputId = getLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.OUTPUT_DEVICE.key,
        null
    );
    const devices = await enumerateAudioDevices();
    const resolved = resolveAudioDeviceIds(devices, storedInputId, storedOutputId, options);
    return { ...resolved, devices };
}

export function isAudioDeviceKind(kind: string): kind is AudioDeviceKind {
    return AUDIO_DEVICE_KINDS.some((audioKind) => audioKind === kind);
}
